import { FRAMES } from "../lib/frames";
import type { SocialMedia } from "../social";
import AuthedImg from "./AuthedImg";

type Props = {
  network: "facebook" | "instagram";
  name: string;
  caption: string;
  media: SocialMedia[];
  frameId: string;
  on?: boolean;
};

function visuel(media: SocialMedia[]): string {
  const first = media.find((m) => m.kind === "image");
  if (!first) return "";
  return first.preview || first.url;
}

function PreviewMedia({ src, video }: { src: string; video: boolean }) {
  if (!src) {
    return <p className="flex h-full items-center justify-center text-xs text-slate-400">{video ? "Vidéo" : "Visuel"}</p>;
  }
  if (/^(blob:|data:)/i.test(src)) {
    return <img src={src} alt="" className="h-full w-full object-cover" />;
  }
  return <AuthedImg path={src} className="h-full w-full object-cover" />;
}

export default function PostPreview({ network, name, caption, media, frameId, on = true }: Props) {
  const frame = FRAMES.find((f) => f.id === frameId) || FRAMES[0];
  const src = visuel(media);
  const video = media.some((m) => m.kind === "video") && !src;
  const ratio = `${frame.w} / ${frame.h}`;

  if (network === "facebook") {
    return (
      <article className={`pr-preview ${on ? "" : "is-off"}`}>
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Aperçu Facebook</p>
        <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white">
          <div className="flex items-center gap-2 px-3 pt-3">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#1877F2] text-[11px] font-bold text-white">f</span>
            <div>
              <p className="text-sm font-semibold leading-4">{name}</p>
              <p className="text-[11px] text-slate-400">À l’instant · Public</p>
            </div>
          </div>
          {caption ? <p className="whitespace-pre-wrap px-3 py-2 text-sm leading-5">{caption}</p> : <p className="px-3 py-2 text-sm text-slate-400">Votre texte apparaîtra ici.</p>}
          <div className="bg-mist" style={{ aspectRatio: ratio }}>
            <PreviewMedia src={src} video={video} />
          </div>
          <div className="grid grid-cols-3 border-t border-slate-100 py-2 text-center text-[11px] text-slate-500">
            <span>J’aime</span>
            <span>Commenter</span>
            <span>Partager</span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className={`pr-preview ${on ? "" : "is-off"}`}>
      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Aperçu Instagram</p>
      <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white">
        <div className="flex items-center gap-2 px-3 py-2">
          <span className="h-7 w-7 rounded-full bg-gradient-to-br from-amber-400 via-pink-500 to-violet-600" />
          <p className="text-sm font-semibold">{name}</p>
        </div>
        <div className="bg-mist" style={{ aspectRatio: ratio }}>
          <PreviewMedia src={src} video={video} />
        </div>
        <div className="space-y-1 px-3 py-2">
          <p className="text-xs text-slate-500">♡ &nbsp; 💬 &nbsp; ➤</p>
          <p className="text-sm leading-5">
            <span className="font-semibold">{name} </span>
            {caption || <span className="text-slate-400">Votre texte apparaîtra ici.</span>}
          </p>
        </div>
      </div>
    </article>
  );
}
